import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateHolidaysForYear,
  selectFacilitators,
} from 'state/Schedule/ScheduleSlice';
import { SCHEDULE_TABS } from 'utils/constants/learningSchedule';

interface UseHolidayNavigationProps {
  scheduleObj: React.RefObject<any>;
  pageToLoad: string;
  isPublicHolidaysViewEnabled: boolean;
  countryCode?: string;
  search?: string;
  facilitattorsViewSearch?: string;
  facilitatorsFilters?: string | null;
  facilitatorsFiltersAreFetched?: boolean;
  onDataBound?: () => void;
}

export const useHolidayNavigation = ({
  scheduleObj,
  pageToLoad,
  isPublicHolidaysViewEnabled,
  countryCode,
  search,
  facilitattorsViewSearch,
  facilitatorsFilters,
  facilitatorsFiltersAreFetched,
  onDataBound,
}: UseHolidayNavigationProps) => {
  const dispatch = useDispatch();
  const previousYearRef = useRef<number | null>(null);
  const previousSearchRef = useRef<string | undefined>(undefined);
  const previousFiltersRef = useRef<string | null | undefined>(undefined);
  const facilitators = useSelector(selectFacilitators);

  // Efficiently update holidays when only year changes, or refetch all if other params changed
  const refetchHolidaysForYear = useCallback(
    (date: Date) => {
      const currentYear = date
        ? new Date(date).getFullYear()
        : new Date().getFullYear();

      // Check if only year changed (not search or filters)
      // Only use efficient update if:
      // 1. We have a previous year (not initial load)
      // 2. Year actually changed
      // 3. Search term hasn't changed
      // 4. Filters haven't changed (for facilitators view)
      //
      // If both year AND search/filters change, onlyYearChanged will be false,
      // and we'll do a full refetch (which includes the new year in optionalParams).
      // This ensures we get updated events AND holidays for the new year.
      const currentSearch =
        pageToLoad === SCHEDULE_TABS.MY_SCHEDULE
          ? search
          : facilitattorsViewSearch;
      const onlyYearChanged =
        previousYearRef.current !== null &&
        previousYearRef.current !== currentYear &&
        previousSearchRef.current === currentSearch &&
        (pageToLoad === SCHEDULE_TABS.MY_SCHEDULE ||
          previousFiltersRef.current === facilitatorsFilters);

      if (
        pageToLoad === SCHEDULE_TABS.MY_SCHEDULE &&
        isPublicHolidaysViewEnabled
      ) {
        if (onlyYearChanged && countryCode) {
          // Efficiently update only holidays without refetching all events
          dispatch(
            updateHolidaysForYear({
              year: currentYear,
              pageType: 'MY_SCHEDULE',
              countryCode,
            })
          );
        }
        previousSearchRef.current = search;
      } else if (
        pageToLoad === SCHEDULE_TABS.FACILITATORS &&
        isPublicHolidaysViewEnabled &&
        facilitatorsFiltersAreFetched
      ) {
        if (onlyYearChanged && facilitators.length > 0) {
          // Efficiently update only holidays without refetching all events
          dispatch(
            updateHolidaysForYear({
              year: currentYear,
              pageType: 'FACILITATORS',
              facilitators,
            })
          );
        }
        previousSearchRef.current = facilitattorsViewSearch;
        previousFiltersRef.current = facilitatorsFilters;
      }

      // Update year reference
      previousYearRef.current = currentYear;
    },
    [
      dispatch,
      pageToLoad,
      search,
      countryCode,
      facilitattorsViewSearch,
      facilitatorsFilters,
      facilitatorsFiltersAreFetched,
      isPublicHolidaysViewEnabled,
      facilitators,
    ]
  );

  // Handle calendar navigation (arrow buttons, view changes)
  const handleNavigating = useCallback(
    (args?: any) => {
      // Use the date from event args if available, otherwise get from scheduleObj
      const newDate =
        args?.currentDate || scheduleObj.current?.selectedDate || new Date();
      refetchHolidaysForYear(newDate);
    },
    [refetchHolidaysForYear, scheduleObj]
  );

  // Handle action completion (catches date picker selections and other actions)
  const handleActionComplete = useCallback(
    (args?: any) => {
      // Check if this is a date navigation action (date picker, navigation buttons, etc.)
      if (
        args?.requestType === 'dateNavigate' ||
        args?.requestType === 'viewNavigate' ||
        args?.action === 'dateNavigate' ||
        args?.action === 'viewNavigate'
      ) {
        // Use date from event args (available immediately) or fallback to scheduleObj
        const newDate =
          args?.currentDate ||
          args?.date ||
          scheduleObj.current?.selectedDate ||
          new Date();
        refetchHolidaysForYear(newDate);
      }
    },
    [refetchHolidaysForYear, scheduleObj]
  );

  // Enhanced dataBound handler to catch date picker selections
  const handleDataBound = useCallback(() => {
    // Call optional callback if provided (e.g., to update current view)
    if (onDataBound) {
      onDataBound();
    }

    // Check if year changed after data is bound (catches date picker selections)
    const shouldCheckYear =
      (pageToLoad === SCHEDULE_TABS.MY_SCHEDULE ||
        pageToLoad === SCHEDULE_TABS.FACILITATORS) &&
      isPublicHolidaysViewEnabled &&
      scheduleObj.current?.selectedDate;

    if (shouldCheckYear) {
      // For facilitators view, also check if filters are fetched
      if (
        pageToLoad === SCHEDULE_TABS.FACILITATORS &&
        !facilitatorsFiltersAreFetched
      ) {
        return;
      }

      refetchHolidaysForYear(scheduleObj.current.selectedDate);
    }
  }, [
    pageToLoad,
    isPublicHolidaysViewEnabled,
    facilitatorsFiltersAreFetched,
    refetchHolidaysForYear,
    scheduleObj,
    onDataBound,
  ]);

  return {
    handleNavigating,
    handleActionComplete,
    handleDataBound,
  };
};
